/**
 * RACKET-LAB-FRONT-1 — RacketLabSettings (Jun 2026).
 *
 * Settings sub-page for the authed Racket Lab shell at
 * `/app/racket-lab/settings`. Read-only mirror of session/profile/scope
 * + language switcher (uses existing LangProvider) + links to admin
 * areas if the role allows. Cero DB writes · cero schema apply.
 */
import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import useRLContext from "@/hooks/useRLContext";
import RacketLabTopBar from "@/components/layout/RacketLabTopBar";
import { resetOnboarding } from "@/components/onboarding/RacketLabOnboardingTour";
import {
  UserCircle2, Languages, Building2, ShieldCheck, ExternalLink, FlaskConical,
  Wrench, KeyRound, RotateCcw,
} from "lucide-react";

const Row = ({ icon: Icon, label, value, testId, accent = "emerald" }) => (
  <div data-testid={testId} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
    <span className="flex items-center gap-2 text-slate-700">
      <Icon className={`h-4 w-4 text-${accent}-700`} />
      <span className="font-medium">{label}</span>
    </span>
    <span className="break-all text-right text-xs text-slate-900">{value || "—"}</span>
  </div>
);

const STAFF_ROLE_KEYS = new Set([
  "admin", "staff", "qa_manager", "qa_tester", "institutional_observer",
]);

const RacketLabSettings = () => {
  const { user } = useAuth();
  const { t, lang, setLang } = useLang();
  const { ctx } = useRLContext();
  if (!user) return <Navigate to="/login" replace />;

  const isStaff = STAFF_ROLE_KEYS.has(user.role);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="rl-app-settings">
      <RacketLabTopBar />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">
        <header className="mb-6">
          <div className="tsw-overline mb-1 text-emerald-700">Racket Lab · Settings</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="rl-settings-title">
            {t("racketLabApp.settings.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">{t("racketLabApp.settings.subtitle")}</p>
        </header>

        <section className="mb-6" data-testid="rl-settings-session">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("racketLabApp.settings.sessionGroup")}
          </h2>
          <div className="grid gap-2">
            <Row icon={UserCircle2} label={t("racketLabApp.settings.user")} value={user.full_name || user.email} testId="rl-settings-user" />
            <Row icon={ShieldCheck} label={t("racketLabApp.settings.role")} value={t(`roles.${user.role}`) || user.role} testId="rl-settings-role" />
            {ctx?.profile && (
              <Row icon={UserCircle2} label={t("racketLabApp.settings.rlProfile")} value={t(`profiles.${ctx.profile}`) || ctx.profile} testId="rl-settings-rl-profile" />
            )}
            {ctx?.organisation_id && (
              <Row icon={Building2} label={t("racketLabApp.settings.organisation")} value={ctx.organisation_id} testId="rl-settings-org" />
            )}
            {ctx?.shop_id && (
              <Row icon={Building2} label={t("racketLabApp.settings.shop")} value={ctx.shop_id} testId="rl-settings-shop" />
            )}
            {isStaff && (
              <Row icon={FlaskConical} label={t("racketLabApp.settings.previewMode")} value={t("racketLabApp.settings.previewActive")} testId="rl-settings-preview" accent="amber" />
            )}
          </div>
        </section>

        <section className="mb-6" data-testid="rl-settings-language">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("racketLabApp.settings.languageGroup")}
          </h2>
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
            <Languages className="h-4 w-4 text-emerald-700" />
            <span className="text-sm text-slate-700">{t("racketLabApp.settings.language")}</span>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="rl-settings-lang-select"
              className="ml-auto rounded border border-slate-200 bg-white px-2 py-1 text-sm"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>
        </section>

        <section className="mb-6" data-testid="rl-settings-onboarding">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("racketLabApp.settings.onboardingGroup")}
          </h2>
          <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-white p-3">
            <div className="text-sm text-slate-700">
              <p className="font-medium">{t("racketLabApp.settings.replayTour")}</p>
              <p className="mt-0.5 text-xs text-slate-500">{t("racketLabApp.settings.replayTourDesc")}</p>
            </div>
            <Link
              to="/app/racket-lab"
              onClick={() => resetOnboarding()}
              data-testid="rl-settings-replay-tour"
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
            >
              <RotateCcw className="h-3.5 w-3.5" /> {t("racketLabApp.settings.replayTourBtn")}
            </Link>
          </div>
        </section>

        <section className="mb-6" data-testid="rl-settings-quick-links">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("racketLabApp.settings.quickLinksGroup")}
          </h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {isStaff && (
              <Link to="/admin/gear/preview" className="flex items-center justify-between rounded-lg border border-emerald-200 bg-white p-3 text-sm hover:bg-emerald-50" data-testid="rl-settings-link-gear-preview">
                <span className="flex items-center gap-2"><Wrench className="h-4 w-4 text-emerald-700" /> My Gear preview</span>
                <ExternalLink className="h-3 w-3 text-slate-400" />
              </Link>
            )}
            {isStaff && (
              <Link to="/admin/gear/store-access" className="flex items-center justify-between rounded-lg border border-amber-200 bg-white p-3 text-sm hover:bg-amber-50" data-testid="rl-settings-link-store-access">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-700" /> Store Access (mock)</span>
                <ExternalLink className="h-3 w-3 text-slate-400" />
              </Link>
            )}
            {isStaff && (
              <Link to="/admin/gear/offer-opportunities" className="flex items-center justify-between rounded-lg border border-rose-200 bg-white p-3 text-sm hover:bg-rose-50" data-testid="rl-settings-link-offers">
                <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-rose-700" /> Offer Opportunities (mock)</span>
                <ExternalLink className="h-3 w-3 text-slate-400" />
              </Link>
            )}
            <Link to="/change-password" className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm hover:bg-slate-50" data-testid="rl-settings-link-change-password">
              <span className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-slate-700" /> {t("common.changePassword") || "Change password"}</span>
              <ExternalLink className="h-3 w-3 text-slate-400" />
            </Link>
            <Link to="/app/racket-lab" className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-sm hover:bg-slate-50" data-testid="rl-settings-link-home">
              <span className="flex items-center gap-2"><Wrench className="h-4 w-4 text-emerald-700" /> {t("racketLabApp.section.home")}</span>
              <ExternalLink className="h-3 w-3 text-slate-400" />
            </Link>
          </div>
        </section>

        <footer className="mt-8 rounded-lg border border-slate-200 bg-white p-3 text-[11px] italic text-slate-600" data-testid="rl-settings-footer-legal">
          {t("racketLabApp.home.footerLegal")}
        </footer>
      </main>
    </div>
  );
};

export default RacketLabSettings;
